import React, { useEffect } from 'react'
import type { PlaybackCore } from '../core/PlaybackCore'

interface FlatSurfaceProps {
  // The persistent media pipeline. The surface never creates or disposes it —
  // it only positions the player element the core already owns.
  core: PlaybackCore
  // The wrapper VideoPlayer renders around the core's video element. It stays
  // mounted in every view (hidden while the sphere uses the element as a
  // texture), so the surface measures it rather than a container of its own.
  containerRef: React.RefObject<HTMLElement | null>
}

// Flat (quadrant / fallback) surface — presentation only. The core owns the
// video element, the video.js player and every media event; all this component
// does is size that player inside the wrapper while the quadrant view is on
// screen. It renders no DOM, so the element never moves and no second media
// pipeline can exist.
const FlatSurface: React.FC<FlatSurfaceProps> = ({ core, containerRef }) => {
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const videoEl = core.videoElement

    // Shortest-dimension layout: scale the video so it fills the
    // container's shortest side (height in landscape, width in portrait),
    // prorate the other side from the video's aspect ratio. The video is
    // centered on BOTH axes so spare space becomes symmetric letterboxing
    // (black bars split evenly above/below or left/right) instead of piling
    // up on one side. The container crops any overflow (overflow: hidden).
    const applyLayout = () => {
      const c = containerRef.current
      const pEl = (core.player.isDisposed() ? null : core.player.el()) as HTMLElement | null
      if (!c || !pEl) return
      const vw = videoEl.videoWidth
      const vh = videoEl.videoHeight
      const cw = c.clientWidth
      const ch = c.clientHeight
      if (!vw || !vh || !cw || !ch) return
      let w: number
      let h: number
      if (cw >= ch) {
        h = ch
        w = (ch * vw) / vh
      } else {
        w = cw
        h = (cw * vh) / vw
      }
      Object.assign(pEl.style, {
        position: 'absolute',
        left: '50%',
        top: '50%',
        bottom: 'auto',
        transform: 'translate(-50%, -50%)',
        width: `${Math.round(w)}px`,
        height: `${Math.round(h)}px`,
      })
    }

    // The element outlives every view switch, so metadata for the current
    // source may already be in hand — lay out immediately as well as on the
    // next load. The observer's initial callback covers a container that is
    // still being sized as the quadrant view appears.
    //
    // `loadedmetadata` alone is NOT enough: Safari's native HLS pipeline fires
    // it once the duration is known, while videoWidth/videoHeight are still 0,
    // so applyLayout bails and never runs again — the player element keeps a
    // zero-sized box and the quadrant view renders black over playing audio.
    // `resize` is the event that actually announces the intrinsic dimensions
    // (and fires again whenever ABR switches rendition); the rest are cheap
    // belt-and-braces for pipelines that report dimensions at other moments.
    const LAYOUT_EVENTS = ['loadedmetadata', 'resize', 'loadeddata', 'canplay'] as const
    for (const name of LAYOUT_EVENTS) videoEl.addEventListener(name, applyLayout)
    const layoutObserver = new ResizeObserver(applyLayout)
    layoutObserver.observe(container)
    applyLayout()

    return () => {
      layoutObserver.disconnect()
      for (const name of LAYOUT_EVENTS) videoEl.removeEventListener(name, applyLayout)
      // Hand the player element back to its stylesheet sizing (video.js
      // `fill`) — the sphere keeps using it as a hidden texture source and
      // must not inherit the quadrant's absolute pixel box.
      const pEl = (core.player.isDisposed() ? null : core.player.el()) as HTMLElement | null
      if (pEl) {
        Object.assign(pEl.style, {
          position: '',
          left: '',
          top: '',
          bottom: '',
          transform: '',
          width: '',
          height: '',
        })
      }
    }
  }, [core, containerRef])

  return null
}

FlatSurface.displayName = 'FlatSurface'
export default FlatSurface
